import { useEffect, useMemo, useRef, useState } from 'react';
import { useDebounce } from '@aimsos/hooks';
import { listWorkspaceUsers } from '../../../services/index.js';

const EMPTY_IDS = [];

/**
 * Loads the real workspace user directory for a people picker (CT-5094) via
 * POST /v1/workspace/user/list_filter. Fetches once when `enabled` flips true
 * (e.g. a Reassign modal opening), keeping the network off the closed state.
 * Returns picker options `[{ id, name, photo }]` plus loading / error.
 *
 * `search` is forwarded to the server as `search.keyword` (CT-5176) — a typed
 * term hits `list_filter` directly instead of being filtered client-side over
 * the cached 100-row page, which never scaled past that cap.
 *
 * `selectedIds` guards against the fetch that follows a selection: picking
 * someone from a search result clears the query, which refetches the plain
 * unsearched page — if the chosen person isn't in THAT page (they were only
 * visible because of the search that just got cleared), they'd silently drop
 * out of `options`, taking the picker's chip/trigger label and any mention
 * built from `options` down with them. A small id→user cache accumulated
 * across fetches keeps a selected person available even when the latest
 * fetch doesn't happen to include them.
 *
 * @param {boolean} enabled - when true, fetch the directory.
 * @param {string} [search] - typed picker query; debounced before it hits the network.
 * @param {string[]} [selectedIds] - currently selected user id(s); never dropped from `users`.
 */
export function useWorkspaceUsers(enabled, search = '', selectedIds = EMPTY_IDS) {
  const [fetchedUsers, setFetchedUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const debouncedSearch = useDebounce(search, 300);
  const cacheRef = useRef(new Map());
  const selectedKey = selectedIds.filter(Boolean).join(',');

  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    const keyword = debouncedSearch.trim();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const { users: fetched } = await listWorkspaceUsers({
          pageSize: 100,
          search: keyword || undefined,
        });
        if (!alive) return;
        fetched.forEach((user) => cacheRef.current.set(user.id, user));
        setFetchedUsers(fetched);
      } catch (err) {
        if (alive) setError(err?.message ?? 'Failed to load users');
      } finally {
        if (alive) setLoading(false);
      }
    };
    load();
    return () => {
      alive = false;
    };
  }, [enabled, debouncedSearch]);

  const users = useMemo(() => {
    const fetchedIds = new Set(fetchedUsers.map((user) => user.id));
    const missingSelected = selectedKey
      .split(',')
      .filter((id) => id && !fetchedIds.has(id) && cacheRef.current.has(id))
      .map((id) => cacheRef.current.get(id));
    return missingSelected.length ? [...missingSelected, ...fetchedUsers] : fetchedUsers;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedKey is the stable primitive proxy for selectedIds
  }, [fetchedUsers, selectedKey]);

  return useMemo(() => ({ users, loading, error }), [users, loading, error]);
}
